import { Helmet } from 'react-helmet-async'

import usePageMatch from '@/shared/hooks/usePageMatch'
import { Utils } from '@/shared/utils'

// Constants
export const BASE_URL = 'https://www.mfinder.co.kr/'
export const IMG_URL = 'https://img.mfinder.co.kr/titleimg/mfinder_og_img.jpg'

// Types
interface Author {
  '@type': 'Organization' | 'Person'
  name: string
}

interface newsListData {
  NewsTitle: string
  RegDT?: string
}

interface faqListData {
  Title: string
  Contents: string
}

interface noticeListData {
  Title: string
  RegDT?: string
}

interface MetaConfig {
  title?: string
  text?: string
  description?: string
  imgSrc?: string
  url?: string
  videoUrl?: string
  videoUploadDate?: string
  datePublished?: string
  faqList?: faqListData[]
  newsList?: newsListData[]
  noticeList?: noticeListData[]
}

// Default configuration
const defaultMetaConfig: Required<Pick<MetaConfig, 'title' | 'description' | 'url' | 'imgSrc'>> = {
  title: '휴대폰분실보호',
  description:
    '휴대폰 분실 시 가장 쉽게 찾는 방법! 분실폰 주변인과 영상통화, 위치 추적, 회수 요청, 데이터 백업 및 삭제까지 한번에',
  url: 'https://www.mfinder.co.kr/home',
  imgSrc: IMG_URL,
}

// Schema generators
const generateNewsSchema = (news: newsListData, author: Author) => ({
  '@type': 'NewsArticle',
  headline: news.NewsTitle,
  author,
})

const generateNoticeSchema = (notice: noticeListData, author: Author) => ({
  '@type': 'Article',
  headline: notice.Title,
  author,
})

const generateFAQSchema = (faq: faqListData) => ({
  '@type': 'Question',
  name: Utils.stripHtml(faq.Title),
  acceptedAnswer: {
    '@type': 'Answer',
    text: Utils.stripHtml(faq.Contents),
  },
})

const generateVideoSchema = (config: {
  videoUrl: string
  description: string | undefined
  title: string | undefined
  videoUploadDate: string
  imgSrc: string | undefined
}) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: config.title,
  description: config.description,
  thumbnailUrl: config.imgSrc,
  uploadDate: config.videoUploadDate,
  contentUrl: config.videoUrl,
  embedUrl: config.videoUrl,
  publisher: {
    '@type': 'Organization',
    name: 'MFinder',
    logo: {
      '@type': 'ImageObject',
      url: '/logo.svg',
    },
  },
})

const generateSoftwareAppSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: '휴대폰분실보호(엠파인더) - 위치추적, 잠금, 백업',
  operatingSystem: 'Android',
  applicationCategory: 'UtilitiesApplication',
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: 4.3,
    ratingCount: 48000,
  },
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'KRW',
  },
  downloadUrl: 'https://play.google.com/store/apps/details?id=kr.co.datau.mfinder',
  publisher: {
    '@type': 'Organization',
    name: 'Data Universe',
    url: 'https://www.datauniverse.co.kr/',
  },
})

function MetaTag({
  title = '휴대폰분실보호',
  description = defaultMetaConfig.description,
  imgSrc = defaultMetaConfig.imgSrc,
  url = defaultMetaConfig.url,
  videoUrl = '',
  videoUploadDate = '',
  newsList = [],
  noticeList = [],
  faqList = [],
}: MetaConfig) {
  const matches = usePageMatch()
  const defaultAuthor: Author = { '@type': 'Organization', name: 'Team MFinder' }

  const generateBreadcrumbList = () => {
    const breadcrumbList: any[] = []

    // Home
    if (matches.isHomePage) {
      breadcrumbList.push({
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${BASE_URL}home`,
      })
    }

    // Intro
    if (matches.isIntroPage) {
      breadcrumbList.push({
        '@type': 'ListItem',
        position: 2,
        name: 'Intro',
        item: `${BASE_URL}intro`,
      })
    }

    // FAQ
    if (matches.isFaqPage) {
      breadcrumbList.push({
        '@type': 'ListItem',
        position: 2,
        name: 'FAQ',
        item: `${BASE_URL}faq`,
      })
    }

    // Center
    if (matches.isCenterPage) {
      breadcrumbList.push({
        '@type': 'ListItem',
        position: 2,
        name: 'Center',
        item: `${BASE_URL}center`,
      })
    }

    // Notice
    if (matches.isNoticePage) {
      breadcrumbList.push({
        '@type': 'ListItem',
        position: 2,
        name: 'Notice',
        item: `${BASE_URL}help/guide`,
      })
    }

    // News
    if (matches.isNewsPage) {
      breadcrumbList.push({
        '@type': 'ListItem',
        position: 2,
        name: 'News',
        item: `${BASE_URL}news`,
      })
    }

    return breadcrumbList
  }

  const INDEXING_PAGE = !(
    matches.isNoticeDetailPage ||
    matches.isTermsPage ||
    matches.isCancelPage ||
    matches.isRefundPage ||
    matches.isAskTermPage
  )

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      {INDEXING_PAGE && <meta name="description" content={description} data-react-helmet="true" />}
      {(matches.isTermsPage ||
        matches.isCancelPage ||
        matches.isNoticeDetailPage ||
        matches.isRefundPage) && <meta name="robots" content="noindex" />}
      {matches.isIntroPage && <meta name="robots" content="index" />}
      {/* OpenGraph Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={title} />
      {INDEXING_PAGE && <meta property="og:title" content={title} />}
      {INDEXING_PAGE && (
        <meta name="og:description" content={description} data-react-helmet="true" />
      )}
      {INDEXING_PAGE && <meta property="og:url" content={url} />}

      {/* Twitter Meta Tags */}
      {INDEXING_PAGE && <meta name="twitter:title" content={title} />}
      {INDEXING_PAGE && (
        <meta name="twitter:description" content={description} data-react-helmet="true" />
      )}
      {INDEXING_PAGE && <link rel="canonical" href={url} />}
      {/* News Schema */}
      {(matches.isNewsPage || matches.isHomePage) && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': newsList?.map((news) => generateNewsSchema(news, defaultAuthor)),
          })}
        </script>
      )}
      {/* Notice Schema */}
      {(matches.isNoticePage || matches.isHomePage) && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': noticeList?.map((guide) => generateNoticeSchema(guide, defaultAuthor)),
          })}
        </script>
      )}
      {/* FAQ Schema */}
      {matches.isFaqPage && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqList?.map(generateFAQSchema),
          })}
        </script>
      )}
      {/* Video Schema */}
      {videoUrl && videoUploadDate && (
        <script type="application/ld+json">
          {JSON.stringify(
            generateVideoSchema({
              title,
              description,
              imgSrc,
              videoUrl,
              videoUploadDate,
            }),
          )}
        </script>
      )}
      {/* Software Application Schema */}
      {matches.isHomePage && (
        <script type="application/ld+json">{JSON.stringify(generateSoftwareAppSchema())}</script>
      )}
      {/* Breadcrumb Schema */}
      {(matches.isIntroPage ||
        matches.isFaqPage ||
        matches.isCenterPage ||
        matches.isNoticePage ||
        matches.isNewsPage) && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: generateBreadcrumbList(),
          })}
        </script>
      )}
    </Helmet>
  )
}

export default MetaTag
